import * as React from "react";
import { useMutation } from "convex/react";
import { toast } from "sonner";

import { api } from "$convex/convexApi";
import { Button } from "@/components/ui/button";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCargosWithDetails } from "@/hooks/useCargos";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { buildCustomerComboboxOptions } from "@/lib/customer-combobox";
import { toUserFacingErrorMessage } from "@/lib/errors";

type PrepaidItem = {
  _id: string;
  cargoId?: string;
  productName?: string;
  totalBags?: number;
  fulfilled?: number;
};

type NewLine = {
  id: string;
  cargoId: string;
  totalBags: string;
};

function createNewLine(): NewLine {
  return { id: crypto.randomUUID(), cargoId: "", totalBags: "" };
}

// Convert an epoch-ms timestamp to a yyyy-mm-dd value for <input type="date"> (local time).
function toDateInput(ms?: number): string {
  if (!ms || !Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Parse a yyyy-mm-dd date input back to an epoch-ms timestamp at local midnight.
function fromDateInput(value: string): number | undefined {
  if (!value) return undefined;
  const [yyyy, mm, dd] = value.split("-").map(Number);
  if (!yyyy || !mm || !dd) return undefined;
  return new Date(yyyy, mm - 1, dd).getTime();
}

export function EditPrepaidInventoryDialog({
  contractId,
  items,
  customerId,
  startDate,
  endDate,
  adminNotes,
}: {
  contractId: string;
  items: PrepaidItem[];
  customerId?: string;
  startDate?: number;
  endDate?: number;
  adminNotes?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [bagsById, setBagsById] = React.useState<Record<string, string>>({});
  const [removedIds, setRemovedIds] = React.useState<Set<string>>(new Set());
  const [newLines, setNewLines] = React.useState<NewLine[]>([]);
  const [customer, setCustomer] = React.useState(customerId ?? "");
  const [start, setStart] = React.useState("");
  const [end, setEnd] = React.useState("");
  const [notes, setNotes] = React.useState(adminNotes ?? "");
  const [submitting, setSubmitting] = React.useState(false);

  const updatePrepaid = useMutation(api.contractWorkflow.adminUpdatePrepaidContract);
  const users = useUsersForImpersonation();
  // Include archived cargos: prepaid coffee bought off-website often sits on an
  // archived/unlisted lot (Available 0) but is physically in the warehouse.
  const cargos = useCargosWithDetails(1000, true);

  const customerOptions = React.useMemo(
    () => buildCustomerComboboxOptions(users as any[] | undefined),
    [users],
  );

  // Cargos already on the contract (so they aren't offered as "add" options).
  const cargoIdsOnContract = React.useMemo(
    () => new Set(items.map((item) => item.cargoId).filter(Boolean) as string[]),
    [items],
  );

  // Prepaid is judged by free physical stock (warehouse - sold), archived or not.
  const eligibleCargos = React.useMemo<any[]>(
    () =>
      Array.isArray(cargos)
        ? cargos.filter(
            (c: any) =>
              Number(c.warehouseBags ?? 0) - Number(c.soldBags ?? 0) > 0 &&
              !cargoIdsOnContract.has(String(c._id)),
          )
        : [],
    [cargos, cargoIdsOnContract],
  );

  const cargoById = React.useMemo<Map<string, any>>(
    () => new Map(eligibleCargos.map((cargo: any) => [cargo._id, cargo])),
    [eligibleCargos],
  );

  const cargoOptions = React.useMemo(() => {
    return eligibleCargos.map((cargo: any) => {
      const freePhysical = Math.max(
        0,
        Number(cargo.warehouseBags ?? 0) - Number(cargo.soldBags ?? 0),
      );
      const originName = String(
        cargo.product?.origin?.name ?? cargo.product?.origin ?? "",
      ).trim();
      // Staff identify lots by ACI/contract number (cargo.contractId); OCI is secondary.
      const aciNumber = String(cargo.contractId ?? "").trim();
      const label = `${aciNumber || cargo.ociNumber || "No ACI"} - ${cargo.product?.name || "Unknown Product"}`;
      const sublabel = [
        originName,
        cargo.ociNumber ? `OCI ${cargo.ociNumber}` : "",
        `${freePhysical} in warehouse`,
      ]
        .filter(Boolean)
        .join(" · ");
      const searchText = [aciNumber, cargo.ociNumber, cargo.product?.name, originName]
        .filter(Boolean)
        .join(" ");
      return { value: cargo._id, label, sublabel: sublabel || undefined, searchText: searchText || undefined };
    });
  }, [eligibleCargos]);

  const initialize = React.useCallback(() => {
    const map: Record<string, string> = {};
    for (const item of items) map[item._id] = String(item.totalBags ?? 0);
    setBagsById(map);
    setRemovedIds(new Set());
    setNewLines([]);
    setCustomer(customerId ?? "");
    setStart(toDateInput(startDate));
    setEnd(toDateInput(endDate));
    setNotes(adminNotes ?? "");
  }, [items, customerId, startDate, endDate, adminNotes]);

  const updateNewLine = (id: string, patch: Partial<NewLine>) => {
    setNewLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const handleSubmit = async () => {
    // Existing (non-removed) lines → bag-count updates.
    const updates: { contractItemId: string; totalBags: number }[] = [];
    for (const item of items) {
      if (removedIds.has(item._id)) continue;
      const bags = Number(bagsById[item._id]);
      const fulfilled = item.fulfilled ?? 0;
      if (!Number.isInteger(bags) || bags <= 0) {
        toast.error(`Enter a whole number of bags greater than 0 for ${item.productName ?? "this coffee"}`);
        return;
      }
      if (bags < fulfilled) {
        toast.error(`${item.productName ?? "This coffee"} can't go below the ${fulfilled} bags already drawn down`);
        return;
      }
      updates.push({ contractItemId: item._id, totalBags: bags });
    }

    // New coffee lines → adds.
    const seenNewCargoIds = new Set<string>();
    const newItems: { cargoId: string; totalBags: number }[] = [];
    for (const line of newLines) {
      if (!line.cargoId) {
        toast.error("Select a coffee for each added line");
        return;
      }
      if (cargoIdsOnContract.has(line.cargoId) || seenNewCargoIds.has(line.cargoId)) {
        toast.error("Each coffee can only appear once on the contract");
        return;
      }
      seenNewCargoIds.add(line.cargoId);

      const bags = Number(line.totalBags);
      if (!Number.isInteger(bags) || bags <= 0) {
        toast.error("Enter a whole number of bags greater than 0 for each added line");
        return;
      }
      const cargo = cargoById.get(line.cargoId);
      const freePhysical = cargo
        ? Math.max(0, Number(cargo.warehouseBags ?? 0) - Number(cargo.soldBags ?? 0))
        : 0;
      if (bags > freePhysical) {
        toast.error(`Only ${freePhysical} bags free in the warehouse for the selected coffee`);
        return;
      }
      newItems.push({ cargoId: line.cargoId, totalBags: bags });
    }

    const removeItemIds = [...removedIds];
    if (updates.length === 0 && newItems.length === 0 && removeItemIds.length === 0 && !customer && !start && !end) {
      toast.error("Nothing to save");
      return;
    }

    const startMs = fromDateInput(start);
    const endMs = fromDateInput(end);
    if (startMs !== undefined && endMs !== undefined && endMs < startMs) {
      toast.error("End date can't be before the start date");
      return;
    }

    setSubmitting(true);
    try {
      await updatePrepaid({
        contractId: contractId as any,
        items: updates as any,
        newItems: newItems.length > 0 ? (newItems as any) : undefined,
        removeItemIds: removeItemIds.length > 0 ? (removeItemIds as any) : undefined,
        customerId: customer && customer !== customerId ? (customer as any) : undefined,
        startDate: startMs !== undefined && startMs !== startDate ? startMs : undefined,
        endDate: endMs !== undefined && endMs !== endDate ? endMs : undefined,
        // Always send (even empty) so clearing the field actually clears the note.
        adminNotes: notes.trim() !== (adminNotes ?? "").trim() ? notes.trim() : undefined,
      });
      toast.success("Prepaid inventory updated");
      setOpen(false);
    } catch (error) {
      toast.error(toUserFacingErrorMessage(error, "Failed to update prepaid inventory"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) initialize();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">Edit Inventory</Button>
      </DialogTrigger>
      {/* sm:max-w-2xl overrides DialogContent's default sm:max-w-lg. */}
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Prepaid Inventory</DialogTitle>
          <DialogDescription>
            Adjust bag counts, add or remove coffees, change the customer or dates.
            Increasing or adding reserves warehouse stock; decreasing or removing releases it.
            You can't go below — or remove — what's already been drawn down.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <Label>Customer</Label>
            <ComboboxSelect
              options={customerOptions}
              value={customer}
              onValueChange={setCustomer}
              placeholder="Select a customer..."
              loading={users === undefined}
              disablePortal
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="prepaid-start-date">Start date</Label>
              <Input
                id="prepaid-start-date"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="prepaid-end-date">End date</Label>
              <Input
                id="prepaid-end-date"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Coffees on this contract</Label>
            {items.map((item) => {
              const removed = removedIds.has(item._id);
              const fulfilled = item.fulfilled ?? 0;
              return (
                <div
                  key={item._id}
                  className={`flex items-end justify-between gap-3 rounded border p-2 ${removed ? "opacity-50" : ""}`}
                >
                  <div className="min-w-0">
                    <p className={`truncate font-medium ${removed ? "line-through" : ""}`}>
                      {item.productName ?? "Coffee"}
                    </p>
                    {fulfilled > 0 ? (
                      <p className="text-xs text-muted-foreground">{fulfilled} drawn down</p>
                    ) : null}
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="w-24">
                      <Label htmlFor={`edit-bags-${item._id}`} className="text-xs">Bags</Label>
                      <Input
                        id={`edit-bags-${item._id}`}
                        type="number"
                        min={Math.max(1, fulfilled || 1)}
                        step={1}
                        inputMode="numeric"
                        disabled={removed}
                        value={bagsById[item._id] ?? ""}
                        onChange={(e) => setBagsById((prev) => ({ ...prev, [item._id]: e.target.value }))}
                      />
                    </div>
                    {removed ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setRemovedIds((prev) => {
                            const next = new Set(prev);
                            next.delete(item._id);
                            return next;
                          })
                        }
                      >
                        Undo
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={fulfilled > 0}
                        title={fulfilled > 0 ? "Has been drawn down — can't remove" : undefined}
                        onClick={() => setRemovedIds((prev) => new Set(prev).add(item._id))}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-3">
            <Label>Add coffee</Label>
            {newLines.map((line) => (
              <div key={line.id} className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <ComboboxSelect
                    options={cargoOptions}
                    value={line.cargoId}
                    onValueChange={(value) => updateNewLine(line.id, { cargoId: value })}
                    placeholder="Select coffee (cargo)..."
                    loading={cargos === undefined}
                    disablePortal
                  />
                </div>
                <div className="w-24">
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    placeholder="Bags"
                    value={line.totalBags}
                    onChange={(e) => updateNewLine(line.id, { totalBags: e.target.value })}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setNewLines((prev) => prev.filter((l) => l.id !== line.id))}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setNewLines((prev) => [...prev, createNewLine()])}
            >
              + Add another coffee
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Saving..." : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
